import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Check, Plus, X,
  BookOpen, Clock, Layers, FileText,
} from 'lucide-react';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STEPS = [
  { id: 'title',       question: 'Quel est le titre de votre formation ?',      hint: '',                            type: 'input',    required: true  },
  { id: 'description', question: 'Décrivez-la en quelques mots',                hint: 'optionnel',                   type: 'textarea', required: false },
  { id: 'hours',       question: 'Durée estimée ?',                             hint: 'en heures, optionnel',        type: 'number',   required: false },
  { id: 'modules',     question: 'Ajoutez les modules de votre formation',      hint: 'au moins 1 requis',           type: 'modules',  required: true  },
  { id: 'review',      question: 'Votre formation est prête !',                 hint: 'Vérifiez et confirmez',       type: 'review',   required: false },
];

export default function CourseCreator() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [hours, setHours] = useState('2');
  const [modules, setModules] = useState([]);
  const [newModuleTitle, setNewModuleTitle] = useState('');
  const [newModuleDesc, setNewModuleDesc] = useState('');

  const current = STEPS[step];
  const progress = (step / (STEPS.length - 1)) * 100;

  const addModule = () => {
    if (!newModuleTitle.trim()) return;
    setModules(prev => [...prev, {
      id: Date.now(),
      title: newModuleTitle.trim(),
      description: newModuleDesc.trim(),
    }]);
    setNewModuleTitle('');
    setNewModuleDesc('');
  };

  const removeModule = (id) => {
    setModules(prev => prev.filter(m => m.id !== id));
  };

  const goNext = () => {
    if (step === 0 && !title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    if (step === 3) {
      // Auto-add module if user typed but didn't click "Ajouter"
      let finalModules = modules;
      if (newModuleTitle.trim()) {
        finalModules = [...modules, { id: Date.now(), title: newModuleTitle.trim(), description: newModuleDesc.trim() }];
        setModules(finalModules);
        setNewModuleTitle('');
        setNewModuleDesc('');
      }
      if (finalModules.length === 0) {
        toast.error('Ajoutez au moins un module');
        return;
      }
    }
    setStep(prev => Math.min(prev + 1, STEPS.length - 1));
  };

  const goPrev = () => {
    setStep(prev => Math.max(prev - 1, 0));
  };

  const handleCreate = async () => {
    if (!title.trim() || modules.length === 0) {
      toast.error('Titre et au moins un module requis');
      return;
    }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/courses/create-manual`, {
        title: title.trim(),
        description: description.trim(),
        estimated_hours: parseFloat(hours) || 1,
        modules: modules.map((m, i) => ({
          title: m.title,
          description: m.description || '',
          order: i + 1,
          blocks: [],
          resources: [],
        })),
      }, { withCredentials: true });
      toast.success('Formation créée avec succès !');
      navigate(`/course/${data.id}/edit`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
      setCreating(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-xl mx-auto px-4 pt-6 pb-10 min-h-[80vh] flex flex-col">

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#64748b]">Étape {step + 1} / {STEPS.length}</span>
            {step > 0 && step < STEPS.length - 1 && (
              <button
                onClick={goPrev}
                className="flex items-center gap-1 text-xs text-[#64748b] hover:text-white transition-colors"
                data-testid="creator-back-btn"
              >
                <ChevronLeft size={13} /> Retour
              </button>
            )}
          </div>
          <div className="h-1 bg-[#2a2935] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#ff6b6b] rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Step content */}
        <div className="flex-1 flex flex-col animate-fade-up">
          <div className="mb-6">
            <h1 className="font-heading text-2xl font-bold text-white">{current.question}</h1>
            {current.hint && (
              <p className="text-sm text-[#64748b] mt-1">{current.hint}</p>
            )}
          </div>

          {/* Input */}
          {current.type === 'input' && (
            <input
              autoFocus
              value={title}
              onChange={e => setTitle(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && goNext()}
              placeholder="ex: Introduction au Machine Learning"
              data-testid="creator-title-input"
              className="w-full bg-[#16161e] border border-[#2a2935] text-white placeholder-[#64748b] rounded-xl px-4 py-4 text-base focus:outline-none focus:border-[#ff6b6b]/50 transition-colors"
            />
          )}

          {/* Textarea */}
          {current.type === 'textarea' && (
            <textarea
              autoFocus
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="ex: Un parcours pratique en 5 modules pour maîtriser les bases..."
              rows={4}
              data-testid="creator-description-input"
              className="w-full bg-[#16161e] border border-[#2a2935] text-white placeholder-[#64748b] rounded-xl px-4 py-4 text-base focus:outline-none focus:border-[#ff6b6b]/50 resize-none transition-colors"
            />
          )}

          {/* Number */}
          {current.type === 'number' && (
            <div className="flex items-center gap-3">
              <input
                autoFocus
                type="number"
                value={hours}
                onChange={e => setHours(e.target.value)}
                min="0.5" max="200" step="0.5"
                data-testid="creator-hours-input"
                className="w-28 bg-[#16161e] border border-[#2a2935] text-white rounded-xl px-4 py-4 text-2xl font-bold text-center focus:outline-none focus:border-[#ff6b6b]/50 transition-colors"
              />
              <span className="text-[#64748b] text-sm">heure(s)</span>
            </div>
          )}

          {/* Modules */}
          {current.type === 'modules' && (
            <div className="space-y-3 flex-1">
              {/* Existing modules */}
              {modules.map((mod, i) => (
                <div
                  key={mod.id}
                  className="bg-[#16161e] border border-[#2a2935] rounded-xl p-3.5 flex items-start gap-2"
                  data-testid={`creator-module-item-${i}`}
                >
                  <div className="w-6 h-6 rounded-md bg-[#ff6b6b]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[#ff6b6b] font-bold" style={{ fontSize: '10px' }}>{i + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium">{mod.title}</p>
                    {mod.description && <p className="text-[#64748b] text-xs mt-0.5">{mod.description}</p>}
                  </div>
                  <button
                    onClick={() => removeModule(mod.id)}
                    className="w-6 h-6 flex items-center justify-center rounded-lg text-[#64748b] hover:text-[#ff6b6b] hover:bg-[#ff6b6b]/10 transition-colors shrink-0"
                    data-testid={`remove-module-${i}`}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}

              {/* Add module form */}
              <div className="bg-[#16161e] border border-[#ff6b6b]/20 rounded-xl p-3.5 space-y-2">
                <p className="text-xs text-[#64748b] mb-1">
                  {modules.length === 0 ? 'Créez votre premier module' : 'Ajouter un module'}
                </p>
                <input
                  value={newModuleTitle}
                  onChange={e => setNewModuleTitle(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addModule()}
                  placeholder="Titre du module (ex: Introduction, Fondamentaux...)"
                  data-testid="creator-module-title-input"
                  className="w-full bg-white/5 border border-white/10 text-white placeholder-[#64748b] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/40 transition-colors"
                />
                <input
                  value={newModuleDesc}
                  onChange={e => setNewModuleDesc(e.target.value)}
                  placeholder="Description courte (optionnelle)"
                  data-testid="creator-module-desc-input"
                  className="w-full bg-white/5 border border-white/10 text-white placeholder-[#64748b] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/40 transition-colors"
                />
                <button
                  onClick={addModule}
                  disabled={!newModuleTitle.trim()}
                  data-testid="creator-add-module-btn"
                  className="flex items-center gap-2 text-sm bg-[#ff6b6b]/10 text-[#ff6b6b] border border-[#ff6b6b]/20 px-4 py-2 rounded-lg hover:bg-[#ff6b6b]/20 disabled:opacity-40 transition-all"
                >
                  <Plus size={13} /> Ajouter ce module
                </button>
              </div>
            </div>
          )}

          {/* Review */}
          {current.type === 'review' && (
            <div className="space-y-4" data-testid="creator-review">
              <div className="bg-[#16161e] border border-[#2a2935] rounded-2xl p-5 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-[#ff6b6b]/10 rounded-xl flex items-center justify-center">
                    <BookOpen size={20} className="text-[#ff6b6b]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-base">{title}</p>
                    {description && <p className="text-[#64748b] text-xs mt-0.5">{description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-5 text-xs text-[#64748b]">
                  <span className="flex items-center gap-1.5">
                    <Clock size={12} /> {hours}h estimées
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Layers size={12} /> {modules.length} module{modules.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="space-y-1.5 pt-1 border-t border-[#2a2935]">
                  {modules.map((mod, i) => (
                    <div key={mod.id} className="flex items-center gap-2.5 text-xs text-[#94a3b8]">
                      <div className="w-5 h-5 rounded-md bg-[#ff6b6b]/10 flex items-center justify-center shrink-0">
                        <span className="text-[#ff6b6b] font-bold" style={{ fontSize: '9px' }}>{i + 1}</span>
                      </div>
                      <span>{mod.title}</span>
                      {mod.description && <span className="text-[#64748b]">— {mod.description}</span>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#20c997]/5 border border-[#20c997]/20 rounded-xl p-3">
                <p className="text-xs text-[#20c997] flex items-center gap-1.5">
                  <FileText size={12} />
                  La formation sera créée et ouverte dans l'éditeur. Vous pourrez ajouter des blocs (texte, quiz, vidéo…) à chaque module.
                </p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="mt-8 flex gap-3">
            {current.type !== 'review' ? (
              <>
                <button
                  onClick={goNext}
                  data-testid="creator-next-btn"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#ff6b6b] text-white px-6 py-3.5 rounded-xl font-bold hover:opacity-90 transition-opacity"
                >
                  Continuer <ChevronRight size={16} />
                </button>
                {!current.required && (
                  <button
                    onClick={goNext}
                    data-testid="creator-skip-btn"
                    className="px-4 py-3.5 rounded-xl border border-[#2a2935] text-[#64748b] text-sm hover:text-white transition-colors"
                  >
                    Ignorer
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={handleCreate}
                disabled={creating}
                data-testid="creator-create-btn"
                className="flex-1 flex items-center justify-center gap-2 bg-[#20c997] text-[#0f0e17] px-6 py-3.5 rounded-xl font-bold hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                {creating ? (
                  <div className="w-4 h-4 rounded-full border-2 border-[#0f0e17] border-t-transparent animate-spin" />
                ) : (
                  <><Check size={16} /> Créer la formation</>
                )}
              </button>
            )}
          </div>

          {/* Back link on step 0 */}
          {step === 0 && (
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-4 text-xs text-[#64748b] hover:text-white flex items-center justify-center gap-1 transition-colors"
            >
              <ChevronLeft size={12} /> Retour au tableau de bord
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
}
